import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, CheckIcon, ClipboardCheckIcon, SearchIcon, ShieldCheckIcon } from 'lucide-react';
import { Button, LinkButton } from '../components/ui/Button';
import { Pill } from '../components/ui/Pill';
import { WhatsAppIcon } from '../components/ui/WhatsAppIcon';
const palette = [{
  name: 'Primario',
  hex: '#1B3A5C',
  note: 'Azul pizarra profundo'
}, {
  name: 'Secundario',
  hex: '#3AAFA9',
  note: 'Verde aqua médico'
}, {
  name: 'Acento cálido',
  hex: '#F7F4EF',
  note: 'Marfil'
}, {
  name: 'Fondo base',
  hex: '#FAFAF8',
  note: 'Blanco cálido'
}, {
  name: 'Texto principal',
  hex: '#1A1A2E',
  note: 'Grafito'
}, {
  name: 'Texto secundario',
  hex: '#6B7280',
  note: 'Gris medio'
}, {
  name: 'CTA WhatsApp',
  hex: '#25A060',
  note: 'Verde conversión'
}, {
  name: 'Error / urgencia',
  hex: '#DC2626',
  note: 'Rojo suave'
}];
const spacing = [{
  label: 'Padding de sección',
  value: '80px desktop / 48px móvil'
}, {
  label: 'Gap interno de componentes',
  value: '24px'
}, {
  label: 'Márgenes laterales',
  value: '120px desktop / 20px móvil'
}, {
  label: 'Border radius',
  value: '12px cards · 8px botones · 50px pills'
}];
function Block({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <section className="border-t border-hairline pt-10" data-id="element-364" data-mp="clxw0mdao6ou">
      <h2 className="font-display text-[26px] font-bold text-ink lg:text-[28px]" data-id="element-365" data-mp="clecj17zxa8p">{title}</h2>
      <div className="mt-6" data-id="element-366" data-mp="7nmk863fvm3z">{children}</div>
    </section>;
}
function Label({
  children
}: {
  children: React.ReactNode;
}) {
  return <p className="mb-3 font-sans text-[13px] italic text-muted" data-id="element-367" data-mp="2ubcm2qsz1b0">{children}</p>;
}
export function DesignSystem() {
  return <div className="min-h-full w-full bg-base" data-id="element-368" data-mp="3p3mdu5pk3vjo">
      <div className="mx-auto max-w-[1100px] px-5 py-12 lg:px-10 lg:py-20" data-id="element-369" data-mp="3p4xi06mwz5bb">
        <header data-id="element-370" data-mp="3l0pqk1p2nb9e">
          <Link to="/" className="inline-flex items-center gap-2 font-sans text-[13px] font-medium text-secondary hover:text-[#2f9a95]" data-id="element-371" data-mp="3l17w5oxmj1tz">
            <ArrowLeftIcon className="h-4 w-4" data-id="element-372" data-mp="36eslh7cokprp" />
            Volver a la página
          </Link>
          <p className="mt-6 font-display text-[32px] font-bold text-ink lg:text-[48px]" data-id="element-373" data-mp="3agw0u72h0ei9">Sonrisa Clara</p>
          <p className="mt-2 font-sans text-[17px] text-muted" data-id="element-374" data-mp="2whwe54yiq9oq">
            Design system · Odontología moderna en Colombia. Médico pero cálido, premium pero alcanzable.
          </p>
        </header>

        <div className="mt-14 flex flex-col gap-14" data-id="element-375" data-mp="3p6l72gpf78pe">
          <Block title="Paleta de color" data-id="element-376" data-mp="3p7wbmiqowkhk">
            <ul className="grid grid-cols-2 gap-6 sm:grid-cols-4" data-id="element-377" data-mp="3u3laskysrzdc">
              {palette.map(color => <li key={color.hex} data-id="element-378" data-mp="3fexluxjginwd">
                  <span className="block h-20 w-full rounded-xl border border-hairline shadow-soft" style={{
                backgroundColor: color.hex
              }} data-id="element-379" data-mp="3fdw7aj38fj9w" />
                  <p className="mt-2 font-sans text-[14px] font-semibold text-ink" data-id="element-380" data-mp="bx6zyuvts5j6">{color.name}</p>
                  <p className="font-sans text-[13px] text-muted" data-id="element-381" data-mp="grjmjdsllwz5">{color.hex}</p>
                  <p className="font-sans text-[13px] italic text-muted" data-id="element-382" data-mp="21eayymk2xyo7">{color.note}</p>
                </li>)}
            </ul>
          </Block>

          <Block title="Tipografía y escala" data-id="element-383" data-mp="2a8pbwih50x45">
            <div className="flex flex-col gap-5 rounded-xl border border-hairline bg-white p-6 shadow-soft" data-id="element-384" data-mp="2aajv9scrgh64">
              <div data-id="element-385" data-mp="26984vkm2brwv">
                <Label data-id="element-386" data-mp="1igzw6rz8on40">H1 · Playfair Display Bold · 48px desktop / 32px móvil</Label>
                <p className="font-display text-[32px] font-bold leading-[1.15] text-ink lg:text-[48px]" data-id="element-387" data-mp="1di6k93zlp45y">
                  Cuida tu sonrisa con claridad
                </p>
              </div>
              <div data-id="element-388" data-mp="1rflfipcjc65u">
                <Label data-id="element-389" data-mp="1mj4sxzkzhzuv">H2 · Playfair Display Bold · 36px / 26px</Label>
                <p className="font-display text-[26px] font-bold text-ink lg:text-[36px]" data-id="element-390" data-mp="1ml7pz3kk0t93">¿Qué te trajo aquí hoy?</p>
              </div>
              <div data-id="element-391" data-mp="2xyjmsuhwcblr">
                <Label data-id="element-392" data-mp="2xx0838bw28m3">H3 · Inter Semibold 600 · 24px / 20px</Label>
                <p className="font-sans text-[20px] font-semibold text-ink lg:text-[24px]" data-id="element-393" data-mp="3t8gi1ktvagmp">Valoración inicial</p>
              </div>
              <div data-id="element-394" data-mp="22orikc6h1zd">
                <Label data-id="element-395" data-mp="225lo1zsy41p">H4 · Inter Semibold 600 · 18px</Label>
                <p className="font-sans text-[18px] font-semibold text-ink" data-id="element-396" data-mp="70qjxvvhudni">Diagnóstico explicado</p>
              </div>
              <div data-id="element-397" data-mp="c0ur2vaihw12">
                <Label data-id="element-398" data-mp="c1e7u8ofeedi">Cuerpo · Inter Regular 400 · 16px / line-height 1.6</Label>
                <p className="max-w-[560px] font-sans text-[16px] leading-[1.6] text-muted" data-id="element-399" data-mp="g159j7fhqewt">
                  Valoraciones, tratamientos y seguimiento en un consultorio diseñado para que llegues con dudas y te
                  vayas con un plan.
                </p>
              </div>
              <div data-id="element-400" data-mp="upjbzh8zw1cu">
                <Label data-id="element-401" data-mp="upt172m7u4hh">Microcopy · Inter Italic · 13px</Label>
                <p className="font-sans text-[13px] italic text-muted" data-id="element-402" data-mp="zqgrfm2mehan">
                  Sin formularios. Te orientamos antes de tu visita.
                </p>
              </div>
            </div>
          </Block>

          <Block title="Espaciado y forma" data-id="element-403" data-mp="2oa31dniq6ym4">
            <ul className="grid gap-4 sm:grid-cols-2" data-id="element-404" data-mp="2o6yrqamhrao2">
              {spacing.map(item => <li key={item.label} className="rounded-xl border border-hairline bg-white p-5 shadow-soft" data-id="element-405" data-mp="2ka2mk1rpslej">
                  <p className="font-sans text-[14px] font-semibold text-ink" data-id="element-406" data-mp="2a9ijquojr8u7">{item.label}</p>
                  <p className="mt-1 font-sans text-[14px] text-muted" data-id="element-407" data-mp="37xat0n0vq0i5">{item.value}</p>
                </li>)}
            </ul>
          </Block>

          <Block title="Botones" data-id="element-408" data-mp="32zisx5q2z82v">
            <div className="flex flex-wrap items-start gap-6" data-id="element-409" data-mp="330k377z9vqjj">
              <div data-id="element-410" data-mp="2y66bpgnqe3k5">
                <Label data-id="element-411" data-mp="2y5eha95vebvg">1 · Primario</Label>
                <Button variant="primary" data-id="element-412" data-mp="2t8widpx817r1">Agendar valoración</Button>
              </div>
              <div data-id="element-413" data-mp="3qnigjws3iid8">
                <Label data-id="element-414" data-mp="3qo1vr5qd2snk">2 · WhatsApp</Label>
                <Button variant="whatsapp" data-id="element-415" data-mp="3qpv6ox80bz1d">
                  <WhatsAppIcon className="h-5 w-5" data-id="element-416" data-mp="3lwjvyl02qy46" />
                  Escribir por WhatsApp
                </Button>
              </div>
              <div data-id="element-417" data-mp="1k01khl1cqg6w">
                <Label data-id="element-418" data-mp="1k1men9jr7x48">3 · Secundario (outline)</Label>
                <Button variant="outline" data-id="element-419" data-mp="1o01yj4kjgpdf">Ver tratamientos</Button>
              </div>
            </div>
          </Block>

          <Block title="Cards" data-id="element-420" data-mp="1a96xgega7cke">
            <div className="grid gap-6 lg:grid-cols-2" data-id="element-421" data-mp="1a7vsibb3nyrp">
              <div data-id="element-422" data-mp="1f7i6imyr5j1k">
                <Label data-id="element-423" data-mp="1f6ymn0pjkkkw">4 · Card de servicio</Label>
                <article className="rounded-2xl border-b-[3px] border-b-transparent bg-white p-7 shadow-soft transition-all hover:-translate-y-1 hover:border-b-secondary hover:shadow-card" data-id="element-424" data-mp="10b9bxypt3tvh">
                  <span className="flex h-11 w-11 items-center justify-center rounded-[10px] bg-mist p-[10px]" data-id="element-425" data-mp="rbdujc5nr2n0">
                    <ClipboardCheckIcon className="h-6 w-6 text-secondary" strokeWidth={1.6} data-id="element-426" data-mp="w95oebpv4ytu" />
                  </span>
                  <h3 className="mt-4 font-sans text-[18px] font-semibold text-ink" data-id="element-427" data-mp="2lq6oz7ut1ndd">Valoración inicial</h3>
                  <p className="mt-2 font-sans text-[14px] leading-[1.6] text-muted" data-id="element-428" data-mp="2qngcuj2jpqz3">
                    Revisión completa, diagnóstico y plan de tratamiento. El punto de partida para cualquier decisión
                    informada.
                  </p>
                </article>
              </div>
              <div data-id="element-429" data-mp="39lrvidtocmfd">
                <Label data-id="element-430" data-mp="39mbc9r7l94rt">5 · Card de diferenciador</Label>
                <article className="flex items-start gap-3 rounded-2xl border border-hairline bg-white p-6 shadow-soft" data-id="element-431" data-mp="34sfjr4a3ga41">
                  <CheckIcon className="mt-[2px] h-5 w-5 shrink-0 text-secondary" strokeWidth={2.5} data-id="element-432" data-mp="3jgto5jn5s6mp" />
                  <p className="font-sans text-[15px] leading-[1.6] text-ink" data-id="element-433" data-mp="3ej1twiz1k8fc">
                    Te explicamos qué encontramos antes de iniciar cualquier procedimiento.
                  </p>
                </article>
              </div>
            </div>
          </Block>

          <Block title="Badges, chips y controles" data-id="element-434" data-mp="1y637dj4ophcz">
            <div className="flex flex-col gap-8" data-id="element-435" data-mp="1y6v2ksuddd2q">
              <div data-id="element-436" data-mp="1u4rsa7jfsej1">
                <Label data-id="element-437" data-mp="1u59lswepsen9">6 · Badge de confianza</Label>
                <div className="flex flex-wrap gap-3" data-id="element-438" data-mp="2iowq56nyhqz8">
                  <Pill data-id="element-439" data-mp="2imajracsxhil">
                    <ShieldCheckIcon className="h-4 w-4" strokeWidth={1.8} data-id="element-440" data-mp="2nn7z7f6ffxge" />
                    Bioseguridad certificada
                  </Pill>
                  <Pill tone="amber" className="text-[11px]" data-id="element-441" data-mp="2sfhjoforw5ba">
                    Contenido demostrativo
                  </Pill>
                  <Pill tone="demo" className="text-[12px]" data-id="element-442" data-mp="2whw6tbzh5r8d">
                    Página demostrativa
                  </Pill>
                </div>
              </div>

              <div data-id="element-443" data-mp="24pewacvsznj0">
                <Label data-id="element-444" data-mp="24ovlrh8hg1f0">7 · Chip de anotación estratégica (modo ORIGEN)</Label>
                <div className="relative w-[240px] rounded-lg bg-primary px-[14px] py-[10px] shadow-chip" data-id="element-445" data-mp="28pfn6x039r6y">
                  <span className="flex items-center gap-2" data-id="element-446" data-mp="2dqv6ajiphnmp">
                    <span className="h-[7px] w-[7px] rounded-full bg-secondary" aria-hidden="true" data-id="element-447" data-mp="3k5vwe8l1vbld" />
                    <span className="font-sans text-[10px] font-bold uppercase tracking-[0.14em] text-secondary" data-id="element-448" data-mp="3p3nwhpvum40n">
                      Criterio ORIGEN
                    </span>
                  </span>
                  <p className="mt-2 font-sans text-[12px] leading-[1.5] text-white/85" data-id="element-449" data-mp="3u915ct9sf4tu">
                    El CTA principal es WhatsApp porque en Colombia convierte 3-4x más que un formulario.
                  </p>
                  <span className="absolute -bottom-1.5 left-6 h-3 w-3 rotate-45 bg-primary" aria-hidden="true" data-id="element-450" data-mp="3lg7l0hfhhf94" />
                </div>
              </div>

              <div data-id="element-451" data-mp="3bit98v4i9en5">
                <Label data-id="element-452" data-mp="3bijq9rhvo7qy">8 · Input de búsqueda</Label>
                <div className="relative max-w-[360px]" data-id="element-453" data-mp="3bkw7g2ftae2y">
                  <SearchIcon className="pointer-events-none absolute left-4 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-muted" data-id="element-454" data-mp="36krnu3jlx38i" />
                  <input type="search" placeholder="Buscar un tratamiento" aria-label="Buscar un tratamiento" className="w-full rounded-lg border border-hairline bg-white py-3 pl-11 pr-4 font-sans text-[15px] text-ink placeholder:text-muted focus:border-secondary focus:outline-none focus:ring-2 focus:ring-secondary/25" data-id="element-455" data-mp="32jy2nhxju2jb" />
                </div>
              </div>

              <div data-id="element-456" data-mp="1maqf6jo162em">
                <Label data-id="element-457" data-mp="1mb9ml6g218ee">9 · Divider decorativo</Label>
                <div className="flex max-w-[360px] items-center gap-3" data-id="element-458" data-mp="1hcyeb05er5ya">
                  <span className="h-px flex-1 bg-hairline" data-id="element-459" data-mp="1havoag499ctk" />
                  <span className="h-[7px] w-[7px] rotate-45 bg-secondary" aria-hidden="true" data-id="element-460" data-mp="tnvyzyy8xp6y" />
                  <span className="h-px flex-1 bg-hairline" data-id="element-461" data-mp="opc9jokg1v9u" />
                </div>
              </div>

              <div data-id="element-462" data-mp="13dp7gr33do6z">
                <Label data-id="element-463" data-mp="13e8gfiarl28v">10 · Avatar placeholder de equipo</Label>
                <div className="flex items-end gap-6" data-id="element-464" data-mp="yl5unwc8ax38">
                  <span className="flex h-[72px] w-[72px] items-center justify-center rounded-full border-[3px] border-secondary shadow-[0_8px_24px_rgba(27,58,92,0.2)]" style={{
                  background: 'linear-gradient(160deg, #1B3A5C 0%, #2A5F8F 100%)'
                }} data-id="element-465" data-mp="yke1es63tbg4">
                    <span className="font-display text-[26px] font-bold leading-none text-white" data-id="element-466" data-mp="2mbli8da7mh0v">CR</span>
                  </span>
                  <div className="flex flex-col items-center gap-3" data-id="element-467" data-mp="28kzy6l90s11e">
                    <span className="flex h-[140px] w-[140px] items-center justify-center rounded-full border-[3px] border-secondary shadow-[0_8px_24px_rgba(27,58,92,0.2)]" style={{
                    background: 'linear-gradient(160deg, #1B3A5C 0%, #2A5F8F 100%)'
                  }} data-id="element-468" data-mp="2cls9dk1gcg0b">
                      <span className="font-display text-[52px] font-bold leading-none text-white" data-id="element-469" data-mp="2rb7wmq1ry3b9">CR</span>
                    </span>
                    <span className="inline-flex items-center rounded-pill border border-secondary/30 bg-secondary/10 px-3 py-1 font-sans text-[11px] font-medium text-secondary" data-id="element-470" data-mp="2w8814wiz6y09">
                      Ejemplo ilustrativo
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </Block>

          <Block title="Ver el sistema aplicado" data-id="element-471" data-mp="3hkp0ye3ttm5f">
            <LinkButton variant="primary" href="/" data-id="element-472" data-mp="3hm090q4b1g2c">
              Ir a la página de Sonrisa Clara
            </LinkButton>
          </Block>
        </div>
      </div>
    </div>;
}